import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Archive, ArchiveRestore, Barcode, Check, ChevronRight, Plus, ScanText, Star, Trash2, X } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Button, BotonEnlace } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Casilla } from '@/components/Casilla';
import { EstadoVacio } from '@/components/Card';
import { Campo, NumberField, TextField, clasesCon } from '@/components/Field';
import {
  NOTA_ALIMENTO_MAXIMA,
  archivarAlimento,
  borradorDesdeBase,
  borrarAlimento,
  guardarAlimento,
  leerAlimento,
  leerMisAlimentos,
  usadoEnPlatillos,
} from '@/db/alimentos';
import { nuevoId } from '@/db/ids';
import { mensajeDeError } from '@/db/tx';
import type { CalidadDato, EstadoCoccion, Food } from '@/db/types';
import { ahoraIso } from '@/domain/dates';
import { formatearNumero, leerDecimal } from '@/domain/numbers';
import {
  CALIDADES,
  ETIQUETAS_CALIDAD,
  NOTAS_CALIDAD,
  calidadDe,
  estadoDe,
  formatearKcal,
  revisarEtiqueta,
} from '@/domain/nutricion';
import { useAlimentosBase } from '@/lib/useAlimentosBase';
import { Etiqueta, EtiquetaCalidad, EtiquetaEstado, Opciones } from './comunes';

function detalle(f: Food): string {
  if (f.referencia.tipo === 'por100g') return `${formatearKcal(f.nutrientes.kcal)} por 100 g`;
  const p = f.referencia.porcion;
  return `${formatearKcal(f.nutrientes.kcal)} por ${p.nombre}${p.gramos === null ? '' : ` (${formatearNumero(p.gramos, { decimales: 0 })} g)`}`;
}

/** Mis alimentos (Req. 20.1, 20.6): los capturados desde su etiqueta. */
export function MisAlimentosPage() {
  const alimentos = useLiveQuery(() => leerMisAlimentos(), []);
  const [verArchivados, setVerArchivados] = useState(false);
  if (alimentos === undefined) return null;
  const activos = alimentos.filter((a) => !a.archivado);
  const archivados = alimentos.filter((a) => a.archivado);

  const lista = (items: readonly Food[]) => (
    <ul className="divide-y divide-borde">
      {items.map((a) => (
        <li key={a.id}>
          <Link to={`/comida/mis-alimentos/${a.id}`} className="flex min-h-14 items-center gap-3 py-3 active:opacity-60">
            <span className="grid min-w-0 flex-1 gap-1">
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="text-[14px] font-medium text-texto">{a.nombre}</span>
                <EtiquetaEstado estado={estadoDe(a)} />
                <EtiquetaCalidad calidad={calidadDe(a)} />
                {a.favorito && (
                  <>
                    <Star aria-hidden="true" className="size-3.5 fill-current text-marca" />
                    <span className="sr-only">Favorito</span>
                  </>
                )}
              </span>
              <span className="text-[12px] text-texto-suave">{detalle(a)}</span>
            </span>
            <ChevronRight className="size-4 text-texto-suave" />
          </Link>
        </li>
      ))}
    </ul>
  );

  return (
    <div>
      <PageHeader
        titulo="Mis alimentos."
        descripcion="Los que capturas desde su etiqueta, para registrarlos en un toque."
        volver={{ a: '/comida', texto: 'Comida', etiqueta: 'Volver a Comida' }}
      />
      <div className="grid gap-2">
        <BotonEnlace to="/comida/mis-alimentos/nuevo" icono={ScanText}>
          Nuevo desde etiqueta
        </BotonEnlace>
        {/* La única pantalla que usa internet, y lo dice ahí (0.13.0) */}
        <BotonEnlace to="/comida/codigo" variante="secundario" icono={Barcode}>
          Buscar por código de barras
        </BotonEnlace>
      </div>

      <div className="mt-6">
        {activos.length === 0 ? (
          <EstadoVacio
            icono={ScanText}
            titulo="Todavía no tienes alimentos propios."
            descripcion="Captura uno con los datos de su etiqueta, por 100 g o por porción. Editarlo después no cambia lo que ya registraste."
          />
        ) : (
          lista(activos)
        )}
      </div>

      {archivados.length > 0 && (
        <section className="mt-6">
          <button
            type="button"
            aria-expanded={verArchivados}
            onClick={() => setVerArchivados((v) => !v)}
            className="inline-flex min-h-11 items-center gap-2 text-[13px] font-medium text-marca"
          >
            <Archive className="size-4" />
            {verArchivados ? 'Ocultar archivados' : `Ver archivados (${archivados.length})`}
          </button>
          {verArchivados && lista(archivados)}
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Alimento desde etiqueta: nuevo, copiado de la base o editado
// ---------------------------------------------------------------------------

export function AlimentoPage() {
  const { id } = useParams();
  const [parametros] = useSearchParams();
  const desdeBase = parametros.get('desdeBase');
  const base = useAlimentosBase();
  const existente = useLiveQuery(async () => (id === undefined ? null : ((await leerAlimento(id)) ?? null)), [id]);
  const [idNuevo] = useState(() => nuevoId('al'));

  if (existente === undefined) return null;
  if (id !== undefined && existente === null) {
    return (
      <div>
        <PageHeader titulo="No está." volver={{ a: '/comida/mis-alimentos', texto: 'Mis alimentos' }} />
        <EstadoVacio titulo="Ese alimento ya no existe." descripcion="Lo que registraste con él no cambia." />
      </div>
    );
  }
  if (existente === null && desdeBase !== null && base.estado === 'cargando') return null;

  const ahora = ahoraIso();
  const alimentoBase = desdeBase !== null && base.estado === 'lista' ? base.modulo.alimentoBase(desdeBase) : undefined;
  const inicial: Food =
    existente ??
    (alimentoBase !== undefined
      ? borradorDesdeBase(alimentoBase, idNuevo)
      : {
          id: idNuevo,
          nombre: '',
          referencia: { tipo: 'porPorcion', porcion: { nombre: 'porción', gramos: null } },
          nutrientes: { kcal: 0, proteinaG: null, carbohidratosG: null, grasaG: null, fibraG: null },
          porciones: [],
          rompeAyuno: true,
          favorito: false,
          archivado: false,
          origenBaseId: null,
          estado: null,
          creadoEn: ahora,
          actualizadoEn: ahora,
        });

  return <FormularioAlimento key={inicial.id} inicial={inicial} nuevo={existente === null} vacio={existente === null && alimentoBase === undefined} />;
}

const texto = (v: number | null) => (v === null ? '' : formatearNumero(v, { decimales: 2, fijos: false }).replace(/,/g, ''));

interface FilaPorcion {
  clave: string;
  nombre: string;
  gramos: string;
}

function FormularioAlimento({ inicial, nuevo, vacio }: { inicial: Food; nuevo: boolean; vacio: boolean }) {
  const navegar = useNavigate();
  const [parametros] = useSearchParams();
  const volver = parametros.get('volver');
  const [nombre, setNombre] = useState(inicial.nombre);
  const [estado, setEstado] = useState<EstadoCoccion | 'ninguno'>(estadoDe(inicial) ?? 'ninguno');
  const [referencia, setReferencia] = useState<'por100g' | 'porPorcion'>(inicial.referencia.tipo);
  const refPorcion = inicial.referencia.tipo === 'porPorcion' ? inicial.referencia.porcion : null;
  const [nombrePorcion, setNombrePorcion] = useState(refPorcion?.nombre ?? 'porción');
  const [gramosPorcion, setGramosPorcion] = useState(texto(refPorcion?.gramos ?? null));
  const [kcal, setKcal] = useState(vacio ? '' : texto(inicial.nutrientes.kcal));
  const [proteina, setProteina] = useState(texto(inicial.nutrientes.proteinaG));
  const [carbohidratos, setCarbohidratos] = useState(texto(inicial.nutrientes.carbohidratosG));
  const [grasa, setGrasa] = useState(texto(inicial.nutrientes.grasaG));
  const [fibra, setFibra] = useState(texto(inicial.nutrientes.fibraG));
  const [porciones, setPorciones] = useState<FilaPorcion[]>(
    inicial.porciones.map((p, i) => ({ clave: `p${i}`, nombre: p.nombre, gramos: texto(p.gramos) })),
  );
  const [rompeAyuno, setRompeAyuno] = useState(inicial.rompeAyuno);
  const [favorito, setFavorito] = useState(inicial.favorito);
  const [calidad, setCalidad] = useState<CalidadDato | 'ninguna'>(calidadDe(inicial) ?? 'ninguna');
  const [notaAlimento, setNotaAlimento] = useState(inicial.nota ?? '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);

  const opcional = (t: string) => (t.trim() === '' ? null : leerDecimal(t));
  const nutrientes = {
    kcal: leerDecimal(kcal) ?? 0,
    proteinaG: opcional(proteina),
    carbohidratosG: opcional(carbohidratos),
    grasaG: opcional(grasa),
    fibraG: opcional(fibra),
  };
  const revision = leerDecimal(kcal) === null ? null : revisarEtiqueta(nutrientes);
  const textoPor = referencia === 'por100g' ? 'por 100 g' : `por ${nombrePorcion.trim() || 'porción'}`;

  function armar(): Food | string {
    if (leerDecimal(kcal) === null) return 'Escribe las kcal de la etiqueta.';
    const campos = [proteina, carbohidratos, grasa, fibra];
    if (campos.some((t) => t.trim() !== '' && leerDecimal(t) === null)) return 'Revisa los macros: deben ser números.';
    const gramosRef = gramosPorcion.trim() === '' ? null : leerDecimal(gramosPorcion);
    if (referencia === 'porPorcion' && gramosPorcion.trim() !== '' && gramosRef === null) {
      return 'Revisa los gramos de la porción.';
    }
    const adicionales = porciones
      .filter((p) => p.nombre.trim() !== '' || p.gramos.trim() !== '')
      .map((p) => ({ nombre: p.nombre, gramos: leerDecimal(p.gramos) }));
    if (adicionales.some((p) => p.gramos === null)) return 'Cada porción adicional necesita sus gramos.';
    if (notaAlimento.trim().length > NOTA_ALIMENTO_MAXIMA) {
      return `La nota puede tener hasta ${NOTA_ALIMENTO_MAXIMA} caracteres.`;
    }
    return {
      ...inicial,
      nombre,
      estado: estado === 'ninguno' ? null : estado,
      calidad: calidad === 'ninguna' ? null : calidad,
      nota: notaAlimento.trim() === '' ? null : notaAlimento,
      referencia:
        referencia === 'por100g'
          ? { tipo: 'por100g' }
          : { tipo: 'porPorcion', porcion: { nombre: nombrePorcion, gramos: gramosRef } },
      nutrientes,
      porciones: adicionales,
      rompeAyuno,
      favorito,
    };
  }

  async function guardar(evento: FormEvent) {
    evento.preventDefault();
    if (guardando) return;
    const alimento = armar();
    if (typeof alimento === 'string') return setError(alimento);
    setError(null);
    setGuardando(true);
    try {
      const guardado = await guardarAlimento(alimento);
      // Desde Agregar, el alimento nuevo se abre listo para registrarlo
      const destino =
        nuevo && volver !== null && volver.startsWith('/comida/agregar')
          ? `/comida/agregar/propio/${guardado.id}${volver.includes('?') ? volver.slice(volver.indexOf('?')) : ''}`
          : '/comida/mis-alimentos';
      void navegar(destino, { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  async function archivar(valor: boolean) {
    setError(null);
    try {
      await archivarAlimento(inicial.id, valor);
      void navegar('/comida/mis-alimentos', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  async function borrar() {
    setError(null);
    try {
      if ((await usadoEnPlatillos(inicial.id)) > 0) {
        setConfirmarBorrar(false);
        return setError('Este alimento está en un platillo: archívalo en lugar de borrarlo.');
      }
      await borrarAlimento(inicial.id);
      void navegar('/comida/mis-alimentos', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  return (
    <div>
      <PageHeader
        titulo={nuevo ? 'Nuevo desde etiqueta.' : 'Tu alimento.'}
        descripcion={
          inicial.origenBaseId !== null && nuevo
            ? 'Copiado de la base: ajusta lo que necesites antes de guardarlo.'
            : 'Copia los datos tal como vienen en la etiqueta.'
        }
        volver={{
          a: volver !== null && volver.startsWith('/comida') ? volver : '/comida/mis-alimentos',
          texto: 'Volver',
        }}
      >
        {inicial.archivado && <Etiqueta tono="aviso">Archivado</Etiqueta>}
      </PageHeader>

      <form onSubmit={(e) => void guardar(e)} className="grid gap-[17px]" noValidate>
        <TextField etiqueta="Nombre" valor={nombre} onCambio={setNombre} maxLength={80} placeholder="Tortillas de nopal" />

        <Opciones
          etiqueta="¿Crudo o cocido?"
          valor={estado}
          onCambio={setEstado}
          opciones={[
            { valor: 'ninguno', etiqueta: 'No aplica' },
            { valor: 'crudo', etiqueta: 'Crudo' },
            { valor: 'cocido', etiqueta: 'Cocido' },
          ]}
        />

        <Opciones
          etiqueta="La etiqueta da los valores"
          valor={referencia}
          onCambio={setReferencia}
          columnas={2}
          opciones={[
            { valor: 'porPorcion', etiqueta: 'Por porción' },
            { valor: 'por100g', etiqueta: 'Por 100 g' },
          ]}
        />

        {referencia === 'porPorcion' && (
          <div className="grid grid-cols-2 gap-3">
            <TextField etiqueta="Porción" valor={nombrePorcion} onCambio={setNombrePorcion} maxLength={30} placeholder="pieza" />
            <NumberField etiqueta="Gramos (opcional)" valor={gramosPorcion} onCambio={setGramosPorcion} />
          </div>
        )}

        <fieldset className="grid gap-3">
          <legend className="mb-1 text-[13px] text-texto-suave">Valores {textoPor}</legend>
          <NumberField etiqueta="kcal" valor={kcal} onCambio={setKcal} />
          <div className="grid grid-cols-2 gap-3">
            <NumberField etiqueta="Proteína (g)" valor={proteina} onCambio={setProteina} />
            <NumberField etiqueta="Carbohidratos (g)" valor={carbohidratos} onCambio={setCarbohidratos} />
            <NumberField etiqueta="Grasa (g)" valor={grasa} onCambio={setGrasa} />
            <NumberField etiqueta="Fibra (g)" valor={fibra} onCambio={setFibra} />
          </div>
          <p className="text-[12px] text-texto-suave">
            Lo que la etiqueta no diga, déjalo vacío: queda como dato incompleto, no como cero.
          </p>
          {revision !== null && <p className="text-[12px] text-aviso">{revision}</p>}
        </fieldset>

        <fieldset className="grid gap-3">
          <legend className="mb-1 text-[13px] text-texto-suave">Otras porciones (opcional)</legend>
          {porciones.map((p, i) => (
            <div key={p.clave} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-2">
              <TextField
                etiqueta={`Porción ${i + 1}`}
                valor={p.nombre}
                onCambio={(v) => setPorciones((ps) => ps.map((x) => (x.clave === p.clave ? { ...x, nombre: v } : x)))}
                maxLength={30}
                placeholder="pieza"
              />
              <NumberField
                etiqueta="Gramos"
                valor={p.gramos}
                onCambio={(v) => setPorciones((ps) => ps.map((x) => (x.clave === p.clave ? { ...x, gramos: v } : x)))}
              />
              <button
                type="button"
                onClick={() => setPorciones((ps) => ps.filter((x) => x.clave !== p.clave))}
                className="grid min-h-12 min-w-11 place-items-center rounded-[13px] bg-marca-suave text-texto"
              >
                <X className="size-4" />
                <span className="sr-only">Quitar la porción {i + 1}</span>
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setPorciones((ps) => [...ps, { clave: `n${Date.now()}`, nombre: '', gramos: '' }])}
            className="inline-flex min-h-11 items-center gap-2 text-[13px] font-medium text-marca"
          >
            <Plus className="size-4" />
            Agregar porción, por ejemplo "1 pieza = 40 g"
          </button>
        </fieldset>

        <div className="space-y-1.5">
          <Opciones
            etiqueta="¿De dónde salen los valores?"
            valor={calidad}
            onCambio={setCalidad}
            opciones={[
              { valor: 'ninguna', etiqueta: 'Sin indicar' },
              ...CALIDADES.map((c) => ({ valor: c, etiqueta: ETIQUETAS_CALIDAD[c] })),
            ]}
          />
          <p className="text-[12px] text-texto-suave">
            {calidad === 'ninguna'
              ? 'Sin indicar se toma como la etiqueta, como siempre.'
              : NOTAS_CALIDAD[calidad]}
          </p>
        </div>

        <Campo etiqueta="Nota (opcional)" ayuda="Fuente, micronutrientes de la etiqueta o cómo se calculó.">
          {({ id, describedBy, hayError }) => (
            <textarea
              id={id}
              value={notaAlimento}
              onChange={(e) => setNotaAlimento(e.target.value)}
              maxLength={NOTA_ALIMENTO_MAXIMA}
              rows={4}
              aria-describedby={describedBy}
              aria-invalid={hayError}
              className={`${clasesCon(hayError)} leading-relaxed`}
            />
          )}
        </Campo>

        <Casilla marcada={!rompeAyuno} onCambio={(m) => setRompeAyuno(!m)}>
          No rompe el ayuno (agua, café o té sin azúcar)
        </Casilla>
        <Casilla marcada={favorito} onCambio={setFavorito}>
          Favorito: aparece primero al buscar
        </Casilla>

        {error !== null && (
          <Banner tono="alerta" titulo="No se guardó">
            {error}
          </Banner>
        )}

        <div className="space-y-2">
          <Button type="submit" icono={Check} disabled={guardando}>
            {guardando ? 'Guardando…' : nuevo ? 'Guardar alimento' : 'Guardar cambios'}
          </Button>
          {!nuevo && (
            <>
              <Button
                variante="secundario"
                icono={inicial.archivado ? ArchiveRestore : Archive}
                onClick={() => void archivar(!inicial.archivado)}
              >
                {inicial.archivado ? 'Sacar del archivo' : 'Archivar'}
              </Button>
              {confirmarBorrar ? (
                <div className="space-y-2 rounded-panel bg-alerta-suave p-4">
                  <p className="text-[13px] text-texto">
                    ¿Borrar este alimento? Lo que ya registraste con él no cambia.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variante="peligro" onClick={() => void borrar()}>
                      Sí, borrar
                    </Button>
                    <Button variante="secundario" onClick={() => setConfirmarBorrar(false)}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variante="fantasma" icono={Trash2} onClick={() => setConfirmarBorrar(true)}>
                  Borrar
                </Button>
              )}
            </>
          )}
        </div>
      </form>
    </div>
  );
}
